import React, { useEffect, useRef, useState } from "react";
import AskOpusOrbIcon from "@src/features/assistantChat/AskOpusOrbIcon";
import Text from "@src/lib/ui/Text";
import { DictationTextarea } from "@src/features/aiAssist/DictationButton";
import { cn } from "@src/lib/utils/cn";
import { Button } from "@src/lib/ui/Button";
import Pen2OutlineIcon from "@src/lib/icons/12px/pen-2-outline";
import { OpusAgentChatTurnStatus } from "@src/types.generated";
import { OpusAgentErrorBubble } from "./OpusAgentErrorBubble";
import { OpusAgentCitations } from "./OpusAgentCitations";
import { OpusAgentFiles } from "./OpusAgentFiles";
import { OpusAgentTurnFeedback } from "./OpusAgentTurnFeedback";
import { OpusAgentEventBlock } from "./OpusAgentEventBlock";
import { outputStatusLine } from "./outputStatusLine";
import { renderMarkdown } from "@src/features/chat/renderTextWithInlineCitations";
import {
  foldToolCallEvents,
  getEventKind,
  highestEventIndex,
  isVisibleTurnBlock,
  stepNoteIndexes,
  outputSteps,
  turnActivity,
  turnCreatedContent,
  turnDidWork,
  type TurnActivityOrb,
} from "./events";
import type {
  OpusAgentTurnState,
  SendMessageOptions,
} from "./useOpusAgentChat";
import { OpusAgentAttachmentPill } from "./attachments/OpusAgentAttachmentPill";
import { OpusAgentPlanCard } from "./OpusAgentPlanCard";
import type { PlanProposalState } from "./planProposals";
import { OpusAgentQuestionsCard } from "./OpusAgentQuestionsCard";
import type { QuestionsState } from "./questionPrompts";

interface OpusAgentTurnMessageProps {
  turn: OpusAgentTurnState;
  turnIndex: number;
  onRetry: (message: string, options?: SendMessageOptions) => void;
  onResumePolling: (turnId: string) => void;
  onEditMessage?: (turnId: string, newMessage: string) => Promise<boolean>;
  // The status row's avatar and, when the turn ends, its finishing beat
  // (see FinishBeat).
  finishBeat?: FinishBeat;
  // Every plan in the chat by event id (planProposalStates): a plan's
  // status depends on the turns after this one.
  plans?: ReadonlyMap<string, PlanProposalState>;
  onApprovePlan?: () => void;
  onViewPlan?: (eventId: string) => void;
  // Every questions event in the chat by event id (questionsStates).
  questions?: ReadonlyMap<string, QuestionsState>;
  onAnswerQuestions?: (message: string, withFile: boolean) => void;
}

export type FinishBeatKind = "done" | "whirl";

/**
 * The status row's avatar, and the finish in place. While the turn runs the
 * row shows the avatar idle, or with its orbiting dots while a step runs or
 * the model reasons, with a line for the current step. When a turn that
 * created content succeeds, the row stays for one spin labelled "Made it",
 * then fades; any other turn's row just goes. Nothing is held back: the
 * reply shows as it arrives. Skipped under reduced motion.
 */
export type FinishBeat = {
  /** The row's icon for its state. */
  renderIcon: (state: TurnActivityOrb | FinishBeatKind) => React.ReactNode;
  /** The beat started; the header, rail and top-nav orbs follow it. */
  onStart: (turnId: string, kind: FinishBeatKind) => void;
};

// How long the row shows the beat after the turn ends; it fades over the
// last FINISH_FADE_MS.
export const FINISH_BEAT_MS: Record<FinishBeatKind, number> = {
  // The whole spin: crouch, hop, turn, land and settle.
  whirl: 2300,
  // The dots wind in and fold into the orb, the checkmark lands, a beat.
  done: 2000,
};
const FINISH_FADE_MS = 220;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const STALE_AFTER_MS = 30_000;

// Chat-bubble layout (design.md › Messaging): agent replies sit in
// bg-bg-tertiary bubbles on the left (one per text block, so a multi-part
// answer reads as consecutive messages), the user's messages in
// bg-action-primary bubbles on the right. Tight: 12/8 padding, 16px corners,
// 4px between bubbles.
export const AGENT_BUBBLE_CLASS =
  "self-start rounded-2xl bg-bg-tertiary px-3 py-2";

// On a column of messages: bubbles that touch read as one group, so the
// corners they share on the left are tucked in (marked with data-bubble).
export const BUBBLE_GROUP_CLASS = cn(
  "flex flex-col gap-1",
  "[&>[data-bubble]+[data-bubble]]:rounded-tl-md",
  "[&>[data-bubble]:has(+[data-bubble])]:rounded-bl-md",
);

// A bubble that arrives while you watch eases in from its tail corner: a
// fade, a 4px rise and a slight grow, 200ms. History never animates.
const BUBBLE_IN =
  "animate-in fade-in slide-in-from-bottom-1 zoom-in-95 duration-200 ease-out motion-reduce:animate-none";

// Quiet reassurance while a turn is RUNNING and nothing else animates
// (before the first event, between slices, during long thinking after a tool
// settled). After 30s with no new event, say so in the same neutral voice:
// a median turn takes about that long, so a quiet stretch is not a fault.
// `icon`: the Ask Opus orb or the agent's bot, idle or with its dots (see
// turnActivity); the Ask Opus orb, thinking, when none is given. `reconnecting`: polls for this turn have been failing or
// hanging for a few seconds. Said quietly, in place of the usual label — a
// short stall on a busy backend is normal and recovers on its own.
// `onlyWhenStalled`: under a reply that is being written, which already
// shows progress, the row stays hidden until the stall or reconnect wording
// has something to say. Exported for /ui/agent's Still working demo.
export const WorkingRow: React.FC<{
  sinceMs: number;
  icon?: React.ReactNode;
  reconnecting?: boolean;
  onlyWhenStalled?: boolean;
  // The current step ("Searching your library", "Thinking", …).
  label?: string;
  // What it is making, one quieter line under the label.
  detail?: string;
  // The finishing beat: its label, and fading out at its end.
  finishLabel?: string;
  fading?: boolean;
}> = ({
  sinceMs,
  label = "Working",
  detail,
  icon,
  reconnecting = false,
  onlyWhenStalled = false,
  finishLabel,
  fading = false,
}) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  const stale = now - sinceMs >= STALE_AFTER_MS;
  if (onlyWhenStalled && !stale && !reconnecting) return null;
  const shown = finishLabel
    ? finishLabel
    : reconnecting
      ? "Reconnecting…"
      : stale
        ? "Still working. Complex questions can take a few minutes."
        : label;
  return (
    <div
      data-testid="opus-agent-working-row"
      data-stale={stale ? "true" : undefined}
      data-reconnecting={reconnecting ? "true" : undefined}
      role="status"
      aria-live="polite"
      data-finish={finishLabel ? "true" : undefined}
      className={cn(
        "mt-1 flex gap-1.5 transition-opacity duration-200",
        "items-center",
        fading && "opacity-0",
      )}
    >
      {icon ?? <AskOpusOrbIcon size={36} state="thinking" />}
      <div className="flex min-w-0 flex-col">
        <Text
          variant="fine-print-title"
          multiline
          // Shimmers while it works; the finish ("Done", "Made it") is still.
          className={cn(
            "min-w-0 text-text-tertiary",
            !finishLabel && "shimmer-text",
          )}
          data-shimmer={finishLabel ? undefined : shown}
        >
          {shown}
        </Text>
        {detail && !finishLabel && (
          <Text
            variant="fine-print-description"
            className="block min-w-0 truncate text-text-tertiary"
            data-testid="opus-agent-output-status"
          >
            {detail}
          </Text>
        )}
      </div>
    </div>
  );
};

export const OpusAgentTurnMessage: React.FC<OpusAgentTurnMessageProps> = ({
  turn,
  turnIndex,
  onRetry,
  onResumePolling,
  onEditMessage,
  finishBeat,
  plans,
  onApprovePlan,
  onViewPlan,
  questions,
  onAnswerQuestions,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editFailed, setEditFailed] = useState(false);
  const pollLost =
    !!turn.pollFailed && turn.status === OpusAgentChatTurnStatus.Running;
  const isRunning =
    turn.status === OpusAgentChatTurnStatus.Running && !pollLost;
  // What was already on screen when this turn mounted (a chat opened with
  // history): those bubbles don't animate in; anything that arrives later,
  // or a turn sent from here, does.
  // A turn sent from this window (it carries a client key), not one that
  // happened to be running when the chat loaded.
  const liveTurnRef = useRef(!!turn.clientKey || !!turn.pending);
  // A preview already streaming when the chat opened is history too.
  const previewAtMountRef = useRef(!!turn.partialText);
  const initialEventIdsRef = useRef(
    new Set(turn.events.map((event) => event.id)),
  );
  // Text that lands in place of a streamed preview already eased in as the
  // preview: it takes over without animating again.
  const knownEventIdsRef = useRef(new Set(initialEventIdsRef.current));
  const quietEventIdsRef = useRef(new Set<string>());
  const hadPreviewRef = useRef(false);
  for (const event of turn.events) {
    if (knownEventIdsRef.current.has(event.id)) continue;
    knownEventIdsRef.current.add(event.id);
    if (hadPreviewRef.current && event.payload.assistantText)
      quietEventIdsRef.current.add(event.id);
  }
  // Only what arrives after mount: reopening a chat mid-turn shows the
  // turn's earlier messages still.
  const animates = (eventId: string) =>
    !initialEventIdsRef.current.has(eventId) &&
    !quietEventIdsRef.current.has(eventId);
  // The message the model is writing right now, ahead of its event. The
  // Working row moves below it and keeps its stall timer, reset as it grows.
  // Shown once there is something to read: a finished sentence or about a
  // line of text, so a bubble doesn't appear with one word and then fill in.
  // Once shown it stays while it grows ("$3." then "$3.50" must not flicker).
  const partial = isRunning && turn.partialText ? turn.partialText : null;
  const previewShownRef = useRef(false);
  if (!partial) {
    previewShownRef.current = false;
    previewAtMountRef.current = false;
  } else if (partial.length >= 60 || /[.!?…]\s/.test(partial))
    previewShownRef.current = true;
  const streamed = previewShownRef.current ? partial : null;
  // The finishing beat (FinishBeat): starts once, when a turn this row saw
  // running succeeds. It plays in the status row at the bottom; the reply is
  // never held back for it.
  const [beat, setBeat] = useState<{
    kind: FinishBeatKind;
    fading: boolean;
  } | null>(null);
  const sawRunningRef = useRef(false);
  const beatStartedRef = useRef(false);
  const beatTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  if (isRunning) sawRunningRef.current = true;
  const succeeded = turn.status === OpusAgentChatTurnStatus.Succeeded;
  useEffect(() => {
    if (!finishBeat || beatStartedRef.current) return;
    if (!sawRunningRef.current || !succeeded) return;
    beatStartedRef.current = true;
    if (prefersReducedMotion()) return;
    // A turn that did real work (created, found or changed something) ends
    // with done: the dots fold home and the checkmark lands ("Made it" when
    // it created content, else "Done"). A plain answer just lands. The spin
    // (whirl) is kept in the orb but not used for now. The header, rail and
    // top-nav orbs play the same beat.
    if (!turnDidWork(turn.events)) return;
    const kind: FinishBeatKind = "done";
    finishBeat.onStart(turn.id, kind);
    setBeat({ kind, fading: false });
    const ms = FINISH_BEAT_MS[kind];
    beatTimersRef.current = [
      setTimeout(
        () => setBeat((b) => b && { ...b, fading: true }),
        ms - FINISH_FADE_MS,
      ),
      setTimeout(() => setBeat(null), ms),
    ];
  }, [finishBeat, succeeded, turn.events, turn.id]);
  useEffect(() => () => beatTimersRef.current.forEach(clearTimeout), []);
  const livePreview = streamed;
  hadPreviewRef.current = livePreview !== null;
  const showWorkingRow = (isRunning && livePreview === null) || !!beat;
  const activity = turnActivity(turn.events);
  const rowState = beat?.kind ?? activity.orb;
  const rowIcon = finishBeat?.renderIcon(rowState) ?? (
    <AskOpusOrbIcon size={36} state={rowState} />
  );
  const finishLabel = beat
    ? turnCreatedContent(turn.events)
      ? "Made it"
      : "Done"
    : undefined;
  const reconnecting = isRunning && !!turn.pollReconnecting;
  // A FAILED turn ending in an error event already shows that error bubble —
  // which then carries the turn's one Try-again (see foldedEvents below).
  const showGenericError =
    pollLost ||
    turn.sendFailed ||
    (turn.status === OpusAgentChatTurnStatus.Failed &&
      !turn.events.some((event) => event.payload.error));
  const attachments = [...(turn.attachments ?? [])].sort(
    (a, b) => a.index - b.index,
  );
  // A retry resends the message with the attachments it was sent with.
  const retry = () =>
    attachments.length
      ? onRetry(turn.userMessage, {
          attachments: attachments.map(({ media, libraryItem }) => ({
            media,
            libraryItem,
          })),
        })
      : onRetry(turn.userMessage);
  const foldedEvents = foldToolCallEvents(turn.events);
  // What the agent is making shows as a line under the status row (the
  // latest one), not as a card: it goes when the result is on screen.
  const latestOutput = outputSteps(turn.events, turn.status).at(-1);
  // What the person sees: the agent's messages and errors in the order they
  // happened, then its plan and question cards, then the status row.
  const blocks = foldedEvents.filter((event) =>
    isVisibleTurnBlock(event.payload),
  );
  const stepNotes = stepNoteIndexes(foldedEvents, succeeded);
  for (const event of foldedEvents)
    if (stepNotes.has(event.index)) quietEventIdsRef.current.add(event.id);
  const renderEvent = (event: OpusAgentTurnState["events"][number]) => (
    <OpusAgentEventBlock
      key={event.id}
      event={event}
      onRetry={event.id === retryEventId ? retry : undefined}
      step={stepNotes.has(event.index)}
    />
  );
  // The retry affordance for a server-side failure lives on its error
  // bubble: only when the failure is the turn's FINAL state (an error event
  // on a turn that recovered, succeeded, or was aborted offers no retry),
  // and on the last error event only — one Try again per turn.
  const retryEventId =
    turn.status === OpusAgentChatTurnStatus.Failed
      ? [...foldedEvents]
          .reverse()
          .find((event) => getEventKind(event.payload) === "error")?.id
      : undefined;
  // When we last heard from the agent, by CLIENT receipt time: reset whenever
  // a new (higher) event index lands; the baseline is this row's mount — the
  // send itself for an optimistic turn, "since we started listening" for a
  // hydrated one. Server timestamps are never used for this.
  const maxEventIndex = highestEventIndex(turn.events);
  const previewLength = streamed?.length ?? 0;
  const lastHeardRef = useRef({
    index: maxEventIndex,
    previewLength,
    at: Date.now(),
  });
  if (
    maxEventIndex > lastHeardRef.current.index ||
    previewLength !== lastHeardRef.current.previewLength
  ) {
    lastHeardRef.current = {
      index: maxEventIndex,
      previewLength,
      at: Date.now(),
    };
  }
  // Edit-and-resend needs a server-side turn: optimistic and failed sends
  // (retry covers those) can't be rewound.
  const canEdit = !!onEditMessage && !turn.pending && !turn.sendFailed;

  const startEditing = () => {
    setDraft(turn.userMessage);
    setEditFailed(false);
    setIsEditing(true);
  };

  const submitEdit = async () => {
    if (!onEditMessage || saving) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === turn.userMessage) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    const edited = await onEditMessage(turn.id, trimmed);
    setSaving(false);
    if (edited) {
      setIsEditing(false);
    } else {
      setEditFailed(true);
    }
  };

  return (
    <>
      {isEditing ? (
        <div className="flex w-full max-w-2xl flex-col items-end gap-1">
          <DictationTextarea
            value={draft}
            onValueChange={setDraft}
            containerClassName="w-full"
            rows={2}
            autoFocus
            data-testid={`edit-user-message-input-${turnIndex}`}
            className="min-h-0"
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submitEdit();
              }
              if (event.key === "Escape") setIsEditing(false);
            }}
          />
          {editFailed && (
            <Text variant="fine-print-title" className="text-status-error-text">
              Could not update this message. Try again.
            </Text>
          )}
          <div className="flex gap-1">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => setIsEditing(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              onClick={submitEdit}
              disabled={saving || !draft.trim()}
              data-testid={`edit-user-message-save-${turnIndex}`}
            >
              Send
            </Button>
          </div>
        </div>
      ) : (
        <div className="group flex w-full max-w-2xl items-center justify-end gap-1">
          {canEdit && (
            <Button
              variant="ghost"
              size="icon-xs"
              className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              onClick={startEditing}
              title="Edit message"
              data-testid={`edit-user-message-${turnIndex}`}
            >
              <Pen2OutlineIcon className="text-icon-tertiary" />
            </Button>
          )}
          <div className="flex max-w-4/5 flex-col items-end gap-1.5">
            {attachments.length > 0 && (
              <div
                className="flex flex-wrap justify-end gap-1.5"
                data-testid={`user-message-attachments-${turnIndex}`}
              >
                {attachments.map((attachment) => (
                  <OpusAgentAttachmentPill
                    key={attachment.id}
                    media={attachment.media}
                    libraryItem={attachment.libraryItem}
                    data-testid="opus-agent-turn-attachment"
                  />
                ))}
              </div>
            )}
            <div
              className={cn(
                "rounded-2xl bg-action-primary px-3 py-2",
                liveTurnRef.current && cn(BUBBLE_IN, "origin-bottom-right"),
              )}
              data-testid={`user-message-bubble-${turnIndex}`}
            >
              <Text
                variant="compact-description"
                multiline
                // Keep the message's own line breaks (a composed questions
                // answer is one line per question). Text's multiline branch
                // sets no inline whiteSpace, so the class applies.
                className="whitespace-pre-wrap text-action-primary-foreground"
              >
                {turn.userMessage}
              </Text>
            </div>
          </div>
        </div>
      )}
      <div data-testid={`ai-message-${turnIndex}`} className="my-2 w-full">
        <Text variant="compact-description" multiline>
          {/* 4px between messages; touching bubbles group (BUBBLE_GROUP_CLASS),
              the status row gets a little more air. */}
          <div
            className={cn(BUBBLE_GROUP_CLASS, "break-words")}
            data-testid={`ai-message-content-${turnIndex}`}
          >
            {blocks.map((event) => {
              const kind = getEventKind(event.payload);
              // Plan and question cards come after the turn's words (below).
              if (kind === "planProposal" || kind === "questions") return null;
              return kind === "assistantText" && !stepNotes.has(event.index) ? (
                <div
                  key={event.id}
                  // A table measures its own room (container query), so a bubble
                  // holding one takes its full width instead of shrinking to fit.
                  className={cn(
                    AGENT_BUBBLE_CLASS,
                    "max-w-17/20 has-[table]:w-17/20",
                    animates(event.id) && cn(BUBBLE_IN, "origin-bottom-left"),
                  )}
                  data-testid="opus-agent-message-bubble"
                  data-bubble
                >
                  {renderEvent(event)}
                </div>
              ) : (
                renderEvent(event)
              );
            })}
            {livePreview !== null && (
              <>
                {/* The same bubble the committed reply lands in, so the
                    text doesn't jump into one when its event arrives. */}
                <div
                  data-testid="opus-agent-live-preview"
                  data-bubble
                  aria-busy="true"
                  className={cn(
                    AGENT_BUBBLE_CLASS,
                    "max-w-17/20 break-words has-[table]:w-17/20",
                    !previewAtMountRef.current &&
                      cn(BUBBLE_IN, "origin-bottom-left"),
                  )}
                >
                  {renderMarkdown(livePreview, { dense: true })}
                </div>
                <span className="sr-only" aria-live="polite">
                  Ask Opus is writing…
                </span>
              </>
            )}
            {/* Plan and question cards always follow the agent's message,
                even when the message was written after the card was sent
                (the tool call lands first), so the agent's line reads above
                the card it introduces. */}
            {blocks.map((event) => {
              const kind = getEventKind(event.payload);
              if (kind === "planProposal") {
                const plan = plans?.get(event.id);
                return plan ? (
                  <div
                    key={event.id}
                    className={cn(
                      "my-1 w-full max-w-[85%]",
                      animates(event.id) && BUBBLE_IN,
                    )}
                  >
                    <OpusAgentPlanCard
                      state={plan}
                      onApprove={() => onApprovePlan?.()}
                      onView={() => onViewPlan?.(event.id)}
                    />
                  </div>
                ) : null;
              }
              if (kind === "questions") {
                const asked = questions?.get(event.id);
                return asked ? (
                  <div
                    key={event.id}
                    className={cn(
                      "my-1 w-full",
                      animates(event.id) && BUBBLE_IN,
                    )}
                  >
                    <OpusAgentQuestionsCard
                      state={asked}
                      onContinue={(message, withFile) =>
                        onAnswerQuestions?.(message, withFile)
                      }
                    />
                  </div>
                ) : null;
              }
              return null;
            })}
            {/* One status row, under everything, cards included: the
                avatar, the current step, and what it is making. Lookups,
                queries, plans and reasoning text stay out of the chat. */}
            {showWorkingRow && (
              <WorkingRow
                sinceMs={lastHeardRef.current.at}
                reconnecting={reconnecting}
                icon={rowIcon}
                label={activity.label}
                detail={latestOutput && outputStatusLine(latestOutput)}
                finishLabel={finishLabel}
                fading={beat?.fading}
              />
            )}
            {livePreview !== null && (
              <WorkingRow
                sinceMs={lastHeardRef.current.at}
                reconnecting={reconnecting}
                icon={rowIcon}
                onlyWhenStalled
              />
            )}
          </div>
          {turn.status === OpusAgentChatTurnStatus.Succeeded &&
            (turn.files?.length ?? 0) > 0 && (
              <OpusAgentFiles files={turn.files ?? []} />
            )}
          {turn.status === OpusAgentChatTurnStatus.Succeeded &&
            turn.citations.length > 0 && (
              <OpusAgentCitations citations={turn.citations} />
            )}
        </Text>
        {/* Rate the finished answer; hidden until the optimistic row has
            its server id, since the mutation needs it. */}
        {succeeded && !turn.pending && !turn.sendFailed && (
          <OpusAgentTurnFeedback
            turnId={turn.id}
            initialFeedback={turn.myFeedback}
          />
        )}
        {turn.status === OpusAgentChatTurnStatus.Aborted && (
          <Text
            variant="fine-print-title"
            className="mt-1 block text-text-tertiary"
          >
            Stopped
          </Text>
        )}
        {showGenericError && (
          <OpusAgentErrorBubble
            message={
              pollLost
                ? turn.pollStranded
                  ? "This response has not updated in a while, so we stopped checking. It may still finish."
                  : "Lost connection while waiting for this response. It may still be running."
                : undefined
            }
            retryLabel={
              pollLost && turn.pollStranded ? "Check again" : undefined
            }
            onRetry={() => (pollLost ? onResumePolling(turn.id) : retry())}
          />
        )}
      </div>
    </>
  );
};
